/**
 * useNetworkGate — calls /api/me on mount to find out whether the caller
 * is on an allowlisted office IP. Used by <RequireNetwork> to swap the
 * dashboard for a "must be on office WiFi" screen otherwise.
 *
 * Soft gate only. The Supabase anon key is in the bundle, so this is
 * casual / drive-by protection, not a security boundary.
 */
import { useQuery } from '@tanstack/react-query';

export interface NetworkGateResult {
  allowed: boolean;
  ip: string;
  country: string;
  reason: string;
}

export function useNetworkGate() {
  return useQuery<NetworkGateResult>({
    queryKey: ['network-gate'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/me', { cache: 'no-store' });
        if (!res.ok) {
          // Function not deployed (e.g. local Vite dev) → open by default
          // so the dashboard isn't blocked during local development.
          return { allowed: true, ip: '', country: '', reason: 'fn_missing' };
        }
        return (await res.json()) as NetworkGateResult;
      } catch {
        return { allowed: true, ip: '', country: '', reason: 'fetch_failed' };
      }
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
